import { NSARouterMakerResult } from "namirasoft-account-react";
import { SortItem } from "namirasoft-core";
import { ClassifierFullRow, ClassifierRow, ConditionInputRow, ConditionOperator, ConditionRow } from "namirasoft-inference";
import { LogGroupRow } from "namirasoft-log";
import { NamirasoftMap } from "namirasoft-site-map";
import { NSBox, NSBoxBoolean, NSBoxEntity, NSBoxEnum, NSBoxInteger, NSBoxString, NSColumn, NSInfoProps, NSRepeater, NSRow, NSTabPage } from "namirasoft-site-react";
import { Component, createRef } from "react";
import ConditionClassifierClassLine from "../components/ConditionClassifierClassLine";
import ConditionStringLine from "../components/ConditionStringLine";
import { InferenceEntities } from "../InferenceEntities";
import { InferenceInfo } from "../InferenceInfo";
import { InferenceLayout } from '../InferenceLayout';
import { InferenceRouterProps } from "../InferenceRouterProps";

interface ConditionEditPageProps extends NSARouterMakerResult, InferenceRouterProps
{
    isEdit: boolean;
}

interface ConditionEditPageState
{
    hasClassifierId: boolean;
    classifierClasses: string[];
}

export default class ConditionEditPage extends Component<ConditionEditPageProps, ConditionEditPageState>
{
    NSBoxString_Name = createRef<NSBoxString>();
    NSBoxBoolean_Not = createRef<NSBoxBoolean>();
    NSBoxEnum_Operator = createRef<NSBoxEnum<ConditionOperator>>();
    NSRepeater_IfTextContainsAllWords = createRef<NSRepeater<ConditionStringLine, string>>();
    NSRepeater_IfTextContainsAnyWords = createRef<NSRepeater<ConditionStringLine, string>>();
    NSBoxInteger_IfLengthGreaterThan = createRef<NSBoxInteger>();
    NSBoxInteger_IfLengthLessThan = createRef<NSBoxInteger>();
    NSBoxEntity_IfClassifierID = createRef<NSBoxEntity<ClassifierRow>>();
    NSRepeater_IfClassifierContainsAllClasses = createRef<NSRepeater<ConditionClassifierClassLine, string>>();
    NSRepeater_IfClassifierContainsAnyClasses = createRef<NSRepeater<ConditionClassifierClassLine, string>>();
    NSRepeater_IfContainsAllTags = createRef<NSRepeater<ConditionStringLine, string>>();
    NSRepeater_IfContainsAnyTags = createRef<NSRepeater<ConditionStringLine, string>>();
    NSBoxEntity_LogGroupID = createRef<NSBoxEntity<LogGroupRow>>();
    NSBoxString_Description = createRef<NSBoxString>();

    private pendingClassifierClassesAll: string[] | null = null;
    private pendingClassifierClassesAny: string[] | null = null;

    constructor(props: ConditionEditPageProps)
    {
        super(props);
        this.state = { hasClassifierId: false, classifierClasses: [] };
        this.onClassifierIdChanged = this.onClassifierIdChanged.bind(this);
        this.getClassifierClassOptions = this.getClassifierClassOptions.bind(this);
    }

    private async onClassifierIdChanged()
    {
        let value = this.NSBoxEntity_IfClassifierID.current?.getValueOne(false) ?? null;
        let has = !!(value && value.trim().length > 0);
        if (!has)
        {
            this.pendingClassifierClassesAll = null;
            this.pendingClassifierClassesAny = null;
            this.setState({ hasClassifierId: false, classifierClasses: [] }, () => this.applyPendingClassifierClasses());
            return;
        }
        let data = this.NSBoxEntity_IfClassifierID.current?.getDataOne(false) as ClassifierFullRow | null;
        let classes: string[];
        if (data && Array.isArray(data.classes))
        {
            classes = data.classes.map(c => c.name).filter((n): n is string => !!n);
        }
        else
        {
            try
            {
                let full = await this.props.servers.inference.classifier.Get(value);
                classes = (full.classes ?? []).map(c => c.name).filter((n): n is string => !!n);
            }
            catch (e)
            {
                console.error(e);
                classes = [];
            }
        }
        let changed = !this.state.hasClassifierId
            || classes.length !== this.state.classifierClasses.length
            || classes.some((c, i) => c !== this.state.classifierClasses[i]);
        if (!changed)
        {
            this.applyPendingClassifierClasses();
            return;
        }
        this.setState({ hasClassifierId: true, classifierClasses: classes }, () => this.applyPendingClassifierClasses());
    }

    private applyPendingClassifierClasses()
    {
        if (this.pendingClassifierClassesAll !== null)
        {
            this.NSRepeater_IfClassifierContainsAllClasses.current?.setValue(this.pendingClassifierClassesAll);
            this.pendingClassifierClassesAll = null;
        }
        if (this.pendingClassifierClassesAny !== null)
        {
            this.NSRepeater_IfClassifierContainsAnyClasses.current?.setValue(this.pendingClassifierClassesAny);
            this.pendingClassifierClassesAny = null;
        }
    }

    private getClassifierClassOptions(): { value: string; title: string; }[]
    {
        return this.state.classifierClasses.map(name => ({ value: name, title: name }));
    }

    private joinWords(values: (string | null)[]): string | null
    {
        let cleaned = values.filter((v): v is string => !!v && v.trim().length > 0).map(v => v.trim());
        if (cleaned.length === 0)
            return null;
        return cleaned.join(",");
    }

    private splitWords(value: string | null): string[]
    {
        if (!value)
            return [];
        return value.split(",").map(v => v.trim()).filter(v => v.length > 0);
    }

    private renderStringRepeater(
        repeaterRef: React.RefObject<NSRepeater<ConditionStringLine, string>>,
        title: string,
        keyPrefix: string,
        itemTitle: string,
        info?: NSInfoProps
    )
    {
        return (
            <NSRepeater<ConditionStringLine, string>
                ref={repeaterRef}
                title={title}
                required={false}
                createItem={(gIndex: number, lIndex: number, ref: React.RefObject<ConditionStringLine>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
                {
                    return <ConditionStringLine
                        ref={ref}
                        key={`${keyPrefix}_${gIndex}`}
                        index={lIndex}
                        title={itemTitle}
                        deletable={onDelete !== undefined}
                        onChanged={() => { onChange(); }}
                        onDelete={() => { onDelete?.(lIndex); }}
                        info={info}
                    />;
                }}
                isEmpty={function (_, value: string | null): boolean
                {
                    return !ConditionStringLine.IS_DIRTY(value);
                }}
                getValue={function (item: ConditionStringLine, checkError: boolean): string | null
                {
                    return item.getValue(checkError);
                }}
                setValue={function (item: ConditionStringLine, _: number, value: string | null): void
                {
                    if (value)
                        item.setValue(value);
                }}
                style={{ width: "100%", maxWidth: NSBox.width.double }}
            />
        );
    }

    private renderClassifierClassRepeater(
        repeaterRef: React.RefObject<NSRepeater<ConditionClassifierClassLine, string>>,
        title: string,
        keyPrefix: string,
        itemTitle: string,
        info?: NSInfoProps
    )
    {
        return (
            <NSRepeater<ConditionClassifierClassLine, string>
                ref={repeaterRef}
                title={title}
                required={false}
                createItem={(gIndex: number, lIndex: number, ref: React.RefObject<ConditionClassifierClassLine>, onChange: () => void, onDelete: undefined | ((index: number) => void)) =>
                {
                    return <ConditionClassifierClassLine
                        ref={ref}
                        key={`${keyPrefix}_${gIndex}`}
                        index={lIndex}
                        title={itemTitle}
                        deletable={onDelete !== undefined}
                        getOptions={this.getClassifierClassOptions}
                        onChanged={() => { onChange(); }}
                        onDelete={() => { onDelete?.(lIndex); }}
                        info={info}
                    />;
                }}
                isEmpty={function (_, value: string | null): boolean
                {
                    return !ConditionClassifierClassLine.IS_DIRTY(value);
                }}
                getValue={function (item: ConditionClassifierClassLine, checkError: boolean): string | null
                {
                    return item.getValue(checkError);
                }}
                setValue={function (item: ConditionClassifierClassLine, _: number, value: string | null): void
                {
                    if (value)
                        item.setValue(value);
                }}
                style={{ width: "100%", maxWidth: NSBox.width.double }}
            />
        );
    }

    override render()
    {
        let namirasoft = new NamirasoftMap(this.props.account.token_manager, console.error);

        return (
            <InferenceLayout<ConditionRow, ConditionInputRow>
                {...this.props}
                action={{ description: this.props.isEdit ? InferenceInfo.condition.edit : InferenceInfo.condition.create }}
                pages={{
                    entity: InferenceEntities.condition(this.props),
                    edit: {
                        ...this.props,
                        ui: {
                            tab_page_edit: {
                                editor: {
                                    getInputRow: (): ConditionInputRow =>
                                    {
                                        let name = this.NSBoxString_Name.current?.getValue() ?? "";
                                        let not = this.NSBoxBoolean_Not.current?.getValue() ?? false;
                                        let operator = (this.NSBoxEnum_Operator.current?.getValueOne() as ConditionOperator | null | undefined) ?? Object.values(ConditionOperator)[0] as ConditionOperator;
                                        let if_text_contains_all_words = this.joinWords(this.NSRepeater_IfTextContainsAllWords.current?.getValue() ?? []);
                                        let if_text_contains_any_words = this.joinWords(this.NSRepeater_IfTextContainsAnyWords.current?.getValue() ?? []);
                                        let if_length_greater_than = this.NSBoxInteger_IfLengthGreaterThan.current?.getValue() ?? null;
                                        let if_length_less_than = this.NSBoxInteger_IfLengthLessThan.current?.getValue() ?? null;
                                        let if_classifier_id = this.NSBoxEntity_IfClassifierID.current?.getValueOne() ?? null;
                                        let if_classifier_contains_all_classes = if_classifier_id
                                            ? this.joinWords(this.NSRepeater_IfClassifierContainsAllClasses.current?.getValue() ?? [])
                                            : null;
                                        let if_classifier_contains_any_classes = if_classifier_id
                                            ? this.joinWords(this.NSRepeater_IfClassifierContainsAnyClasses.current?.getValue() ?? [])
                                            : null;
                                        let if_contains_all_tags = this.joinWords(this.NSRepeater_IfContainsAllTags.current?.getValue() ?? []);
                                        let if_contains_any_tags = this.joinWords(this.NSRepeater_IfContainsAnyTags.current?.getValue() ?? []);
                                        let log_group_id = this.NSBoxEntity_LogGroupID.current?.getValueOne() ?? null;
                                        let description = this.NSBoxString_Description.current?.getValue() ?? null;
                                        return {
                                            workspace_id: null,
                                            name,
                                            not,
                                            operator,
                                            if_text_contains_any_words,
                                            if_text_contains_all_words,
                                            if_length_greater_than,
                                            if_length_less_than,
                                            if_classifier_id,
                                            if_classifier_contains_any_classes,
                                            if_classifier_contains_all_classes,
                                            if_contains_any_tags,
                                            if_contains_all_tags,
                                            log_group_id,
                                            description,
                                            condition_category: [],
                                            condition_field: [],
                                            condition_tag: [],
                                        };
                                    },
                                    setRow: (row: ConditionRow) =>
                                    {
                                        this.NSBoxString_Name.current?.setValue(row.name);
                                        this.NSBoxBoolean_Not.current?.setValueBool(row.not);
                                        this.NSBoxEnum_Operator.current?.setValue(row.operator);
                                        this.NSRepeater_IfTextContainsAllWords.current?.setValue(this.splitWords(row.if_text_contains_all_words));
                                        this.NSRepeater_IfTextContainsAnyWords.current?.setValue(this.splitWords(row.if_text_contains_any_words));
                                        this.NSBoxInteger_IfLengthGreaterThan.current?.setValue(row.if_length_greater_than);
                                        this.NSBoxInteger_IfLengthLessThan.current?.setValue(row.if_length_less_than);
                                        this.pendingClassifierClassesAll = this.splitWords(row.if_classifier_contains_all_classes);
                                        this.pendingClassifierClassesAny = this.splitWords(row.if_classifier_contains_any_classes);
                                        this.NSBoxEntity_IfClassifierID.current?.setValue(row.if_classifier_id);
                                        this.NSRepeater_IfContainsAllTags.current?.setValue(this.splitWords(row.if_contains_all_tags));
                                        this.NSRepeater_IfContainsAnyTags.current?.setValue(this.splitWords(row.if_contains_any_tags));
                                        this.NSBoxEntity_LogGroupID.current?.setValue(row.log_group_id);
                                        this.NSBoxString_Description.current?.setValue(row.description);
                                    }
                                },
                                ui: {
                                    extra_tabs: [
                                        {
                                            title: "If Text",
                                            clicked: true,
                                            getContent: () => (
                                                <NSTabPage
                                                    tabs={[
                                                        {
                                                            title: "Contains All",
                                                            clicked: true,
                                                            getContent: () => this.renderStringRepeater(
                                                                this.NSRepeater_IfTextContainsAllWords,
                                                                "If Text Contains All Words",
                                                                "ConditionWordAll",
                                                                "Word",
                                                                { link: "https://namirasoft.com/inference/console-guide/condition/#field-if-contains-words" }
                                                            )
                                                        },
                                                        {
                                                            title: "Contains Any",
                                                            clicked: true,
                                                            getContent: () => this.renderStringRepeater(
                                                                this.NSRepeater_IfTextContainsAnyWords,
                                                                "If Text Contains Any Words",
                                                                "ConditionWordAny",
                                                                "Word",
                                                                { link: "https://namirasoft.com/inference/console-guide/condition/#field-if-contains-words" }
                                                            )
                                                        }
                                                    ]}
                                                />
                                            )
                                        },
                                        {
                                            title: "If Length",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxInteger
                                                        ref={this.NSBoxInteger_IfLengthGreaterThan}
                                                        title="If Length Greater Than"
                                                        required={false}
                                                        style={{ width: NSBox.width.double }}
                                                        info={{ link: "https://namirasoft.com/inference/console-guide/condition/#field-if-length-greater-than" }}
                                                    />
                                                    <NSBoxInteger
                                                        ref={this.NSBoxInteger_IfLengthLessThan}
                                                        title="If Length Less Than"
                                                        required={false}
                                                        style={{ width: NSBox.width.double }}
                                                        info={{ link: "https://namirasoft.com/inference/console-guide/condition/#field-if-length-less-than" }}
                                                    />
                                                </NSColumn>
                                            )
                                        },
                                        {
                                            title: "If Classifier",
                                            clicked: true,
                                            getContent: () => (
                                                <NSColumn>
                                                    <NSBoxEntity<ClassifierRow>
                                                        ref={this.NSBoxEntity_IfClassifierID}
                                                        {...this.props}
                                                        title="If Classifier ID"
                                                        required={false}
                                                        multiple={false}
                                                        info={{ link: "https://namirasoft.com/inference/console-guide/condition/#field-if-classifier" }}
                                                        table={namirasoft.inference.tables.classifier}
                                                        getValue={(item) => item.id}
                                                        getTitle={(item) => item.name}
                                                        getSort={() => new SortItem(namirasoft.inference.tables.classifier, namirasoft.inference.tables.classifier.columns.name, true)}
                                                        onChanged={this.onClassifierIdChanged}
                                                        style={{ width: NSBox.width.double }}
                                                    />
                                                    {this.state.hasClassifierId && (
                                                        <NSTabPage
                                                            key={`classifier-classes-${this.state.classifierClasses.length}:${this.state.classifierClasses.join("|")}`}
                                                            tabs={[
                                                                {
                                                                    title: "Contains All",
                                                                    clicked: true,
                                                                    getContent: () => this.renderClassifierClassRepeater(
                                                                        this.NSRepeater_IfClassifierContainsAllClasses,
                                                                        "If Classifier Contains All Classes",
                                                                        "ConditionClassAll",
                                                                        "Class",
                                                                        { link: "https://namirasoft.com/inference/console-guide/condition/#field-if-classifier" }
                                                                    )
                                                                },
                                                                {
                                                                    title: "Contains Any",
                                                                    clicked: true,
                                                                    getContent: () => this.renderClassifierClassRepeater(
                                                                        this.NSRepeater_IfClassifierContainsAnyClasses,
                                                                        "If Classifier Contains Any Classes",
                                                                        "ConditionClassAny",
                                                                        "Class",
                                                                        { link: "https://namirasoft.com/inference/console-guide/condition/#field-if-classifier" }
                                                                    )
                                                                }
                                                            ]}
                                                        />
                                                    )}
                                                </NSColumn>
                                            )
                                        },
                                        {
                                            title: "If Tags",
                                            clicked: true,
                                            getContent: () => (
                                                <NSTabPage
                                                    tabs={[
                                                        {
                                                            title: "Contains All",
                                                            clicked: true,
                                                            getContent: () => this.renderStringRepeater(
                                                                this.NSRepeater_IfContainsAllTags,
                                                                "If Contains All Tags",
                                                                "ConditionTagAll",
                                                                "Tag",
                                                                { link: "https://namirasoft.com/inference/console-guide/condition/#field-if-contains-all-tags" }
                                                            )
                                                        },
                                                        {
                                                            title: "Contains Any",
                                                            clicked: true,
                                                            getContent: () => this.renderStringRepeater(
                                                                this.NSRepeater_IfContainsAnyTags,
                                                                "If Contains Any Tags",
                                                                "ConditionTagAny",
                                                                "Tag",
                                                                { link: "https://namirasoft.com/inference/console-guide/condition/#field-if-contains-any-tags" }
                                                            )
                                                        }
                                                    ]}
                                                />
                                            )
                                        }
                                    ]
                                }
                            }
                        }
                    }
                }}
            >
                <NSColumn>
                    <NSBoxString
                        ref={this.NSBoxString_Name}
                        required
                        title="Name"
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/condition/#field-name" }}
                    />
                    <NSRow>
                        <NSBoxBoolean
                            ref={this.NSBoxBoolean_Not}
                            headerTitle="Not"
                            title="Invert the condition result"
                            required={false}
                            info={{ link: "https://namirasoft.com/inference/console-guide/condition/#field-not" }}
                        />
                        <NSBoxEnum<ConditionOperator>
                            ref={this.NSBoxEnum_Operator}
                            title="Operator"
                            required={false}
                            multiple={false}
                            getEnumObject={() => ConditionOperator}
                            info={{ link: "https://namirasoft.com/inference/console-guide/condition/#field-operator" }}
                        />
                    </NSRow>
                    <NSBoxEntity<LogGroupRow>
                        ref={this.NSBoxEntity_LogGroupID}
                        {...this.props}
                        title="Log Group"
                        required={false}
                        multiple={false}
                        info={{ link: "https://namirasoft.com/inference/console-guide/condition/#field-log-group" }}
                        table={namirasoft.log.tables.log_group}
                        getValue={(item) => item.id}
                        getTitle={(item) => item.name}
                        getSort={() => new SortItem(namirasoft.log.tables.log_group, namirasoft.log.tables.log_group.columns.name, true)}
                        style={{ width: NSBox.width.double }}
                    />
                    <NSBoxString
                        ref={this.NSBoxString_Description}
                        required={false}
                        title="Description"
                        style={{ width: NSBox.width.double }}
                        info={{ link: "https://namirasoft.com/inference/console-guide/condition/#field-description" }}
                    />
                </NSColumn>
            </InferenceLayout>
        );
    }
}